import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../../utils/api';

/**
 * Hook för att hantera globala interna förklaringar för estimat-moment
 * Förklaringarna delas mellan alla estimat och alla byggkonsulter
 */
const useInternalNotes = () => {
  const [internalNotes, setInternalNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Hämta alla interna förklaringar från backend
   */
  const fetchInternalNotes = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/internal-notes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Kunde inte hämta interna förklaringar');
      }
      
      const result = await response.json();
      
      if (result.success) {
        setInternalNotes(result.data || {});
      }
      
      setError(null);
    } catch (err) {
      console.error('❌ Fel vid hämtning av interna förklaringar:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Spara eller uppdatera en intern förklaring
   */
  const saveInternalNote = useCallback(async (itemKey, note) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/internal-notes/${encodeURIComponent(itemKey)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Kunde inte spara intern förklaring');
      }
      
      const result = await response.json();
      
      if (result.success) {
        // Uppdatera lokal state
        setInternalNotes(prev => ({
          ...prev,
          [itemKey]: {
            note: result.data.note,
            updatedAt: result.data.updatedAt
          }
        }));
      }
      
      return { success: true };
    } catch (err) {
      console.error('❌ Fel vid sparande av intern förklaring:', err);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Ta bort en intern förklaring
   */
  const deleteInternalNote = useCallback(async (itemKey) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/internal-notes/${encodeURIComponent(itemKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Kunde inte ta bort intern förklaring');
      }
      
      // Uppdatera lokal state
      setInternalNotes(prev => {
        const updated = { ...prev };
        delete updated[itemKey];
        return updated;
      });
      
      return { success: true };
    } catch (err) {
      console.error('❌ Fel vid borttagning av intern förklaring:', err);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Hämta intern förklaring för en specifik item
   */
  const getInternalNote = useCallback((itemKey) => {
    return internalNotes[itemKey]?.note || '';
  }, [internalNotes]);

  /**
   * Kontrollera om en item har en intern förklaring
   */
  const hasInternalNote = useCallback((itemKey) => {
    return !!internalNotes[itemKey]?.note;
  }, [internalNotes]);

  // Hämta förklaringar vid mount
  useEffect(() => {
    fetchInternalNotes();
  }, [fetchInternalNotes]);

  return {
    internalNotes,
    loading,
    error,
    fetchInternalNotes,
    saveInternalNote,
    deleteInternalNote,
    getInternalNote,
    hasInternalNote
  };
};

export default useInternalNotes;

